import {
	ActionMap,
	FormContext_Action_PayloadMap,
	FormContext_Actions,
	formContext_reducer,
	isFormContext_Action_PayloadMap,
} from '@/components/forms/FormContext';
import { ReplaceInPersonFormState, StepMap } from './constants';
import { match } from '@/helpers/match';
import logError from '@/helpers/logError';
import {
	AddressStepActionMap,
	AddressStepActions,
	addressStepReducer,
	isAddressStepActionMap,
} from './steps/AddressStep/state';
import {
	ContactInfoStepActionMap,
	ContactInfoStepActions,
	contactInfoStepReducer,
	isContactInfoStepActionMap,
} from './steps/ContactInfoStep/state';
import {
	isSystemIssuesStepActionMap,
	SystemIssuesStepActionMap,
	SystemIssuesStepActions,
	systemIssuesStepReducer,
} from './steps/SystemIssuesStep/state';
import {
	isPictureStepActionMap,
	PictureStepActionMap,
	PictureStepActions,
	pictureStepReducer,
} from './steps/PictureStep/state';
import userProfile from '@/helpers/userProfile';
import {
	isMessageStepActionMap,
	MessageStepActionMap,
	MessageStepActions,
	messageStepReducer,
} from './steps/MessageStep/state';
import {
	isSystemAgeStepActionMap,
	SystemAgeStepActionMap,
	SystemAgeStepActions,
	systemAgeStepReducer,
} from './steps/SystemAgeStep/state';
import {
	isBuyingDecisionStepActionMap,
	BuyingDecisionStepActionMap,
	BuyingDecisionStepActions,
	buyingDecisionStepReducer,
} from './steps/BuyingDecisionStep/state';
import {
	isUrgencyStepActionMap,
	UrgencyStepActionMap,
	UrgencyStepActions,
	UrgencyStepReducer,
} from './steps/UrgencyStep/state';

/**
 * These are not step specific actions, they are actions that can be handled by
 * the form context
 */
export const ReplaceInPersonFormActions = {
	SET_HUBSPOT_DEAL_ID: 'SET_HUBSPOT_DEAL_ID',
	HYDRATE_STATE: 'HYDRATE_STATE',
} as const;

/**
 * The payloads for the non-step specific actions
 */
export interface ReplaceInPersonActionPayload {
	[ReplaceInPersonFormActions.SET_HUBSPOT_DEAL_ID]: { hubspotDealId: string };
	[ReplaceInPersonFormActions.HYDRATE_STATE]: ReplaceInPersonFormState;
}

/**
 * The map for what payload corresponds to what action also, not step specific
 */
export type ReplaceInPersonNoStepActionMap =
	ActionMap<ReplaceInPersonActionPayload>[keyof ActionMap<ReplaceInPersonActionPayload>];

/**
 * The map for all the actions that can be handled by the reducer. If you add a
 * new step, you will need to add the action map for that step here.
 */
export type ReplaceInPersonFormActionMap =
	| FormContext_Action_PayloadMap<StepMap>
	| UrgencyStepActionMap
	| AddressStepActionMap
	| ContactInfoStepActionMap
	| SystemIssuesStepActionMap
	| PictureStepActionMap
	| BuyingDecisionStepActionMap
	| MessageStepActionMap
	| SystemAgeStepActionMap
	| ReplaceInPersonNoStepActionMap;

/**
 * The rootReducer for the form. This is where all the actions are handled. FYI
 * order does not matter.
 *
 * If you add a new step, you will need to add the reducer for that step here.
 */
export const rootReducer = (
	state: ReplaceInPersonFormState,
	action: ReplaceInPersonFormActionMap,
): ReplaceInPersonFormState => {
	const [, updateProfile] = userProfile();
	const updatedState = match<ReplaceInPersonFormState>(
		[
			// This is the only action the form context will handle
			action.type in FormContext_Actions,
			(): ReplaceInPersonFormState => {
				if (!isFormContext_Action_PayloadMap<StepMap>(action)) return state;
				return formContext_reducer<StepMap, ReplaceInPersonFormState>(
					state,
					action,
				);
			},
		],
		[
			action.type in UrgencyStepActions,
			(): ReplaceInPersonFormState => {
				if (!isUrgencyStepActionMap(action)) return state;
				return UrgencyStepReducer(state, action);
			},
		],
		[
			action.type in AddressStepActions,
			(): ReplaceInPersonFormState => {
				if (!isAddressStepActionMap(action)) return state;
				return addressStepReducer(state, action);
			},
		],
		[
			action.type in ContactInfoStepActions,
			(): ReplaceInPersonFormState => {
				if (!isContactInfoStepActionMap(action)) return state;
				return contactInfoStepReducer(state, action);
			},
		],
		[
			action.type in SystemIssuesStepActions,
			(): ReplaceInPersonFormState => {
				if (!isSystemIssuesStepActionMap(action)) return state;
				return systemIssuesStepReducer(state, action);
			},
		],
		[
			action.type in PictureStepActions,
			(): ReplaceInPersonFormState => {
				if (!isPictureStepActionMap(action)) return state;
				return pictureStepReducer(state, action);
			},
		],
		[
			action.type in BuyingDecisionStepActions,
			(): ReplaceInPersonFormState => {
				if (!isBuyingDecisionStepActionMap(action)) return state;
				return buyingDecisionStepReducer(state, action);
			},
		],
		[
			action.type in MessageStepActions,
			(): ReplaceInPersonFormState => {
				if (!isMessageStepActionMap(action)) return state;
				return messageStepReducer(state, action);
			},
		],
		[
			action.type in SystemAgeStepActions,
			(): ReplaceInPersonFormState => {
				if (!isSystemAgeStepActionMap(action)) return state;
				return systemAgeStepReducer(state, action);
			},
		],
		[
			action.type in ReplaceInPersonFormActions,
			(): ReplaceInPersonFormState => {
				switch (action.type) {
					case ReplaceInPersonFormActions.SET_HUBSPOT_DEAL_ID:
						updateProfile('dealId', action.payload.hubspotDealId);
						return {
							...state,
							hubspotDealId: action.payload.hubspotDealId,
						};
					case ReplaceInPersonFormActions.HYDRATE_STATE:
						return {
							...state,
							...action.payload,
							/**
							 * We don't want to hydrate the steps because they have functions, which are not serializable
							 * so we don't want to overwrite them with the ones pulled from the server because the server
							 * only contains serializable data.
							 */
							steps: state.steps,
						};
					default:
						return state;
				}
			},
		],
	);
	if (!updatedState)
		throw logError(new Error('rootReducer failed to return a state'));
	return updatedState;
};
